precision highp float;

uniform float uTime;
uniform vec2 uResolution;
uniform float uBass;
uniform float uMid;
uniform float uHigh;
uniform float uAudioAverage;
uniform float uZoom;
uniform vec2 uOffset;
uniform vec3 uColorPrimary;
uniform vec3 uColorSecondary;
uniform vec3 uColorAccent;
uniform vec2 uDeviceTilt;

varying vec2 vUv;

const int MAX_ITERATIONS = 150;
const float PI = 3.14159265359;

// 2D rotation matrix
mat2 rotate2D(float angle) {
  float c = cos(angle);
  float s = sin(angle);
  return mat2(c, -s, s, c);
}

// Complex multiplication
vec2 cmul(vec2 a, vec2 b) {
  return vec2(a.x * b.x - a.y * b.y, a.x * b.y + a.y * b.x);
}

// Complex power
vec2 cpow(vec2 z, float n) {
  float r = length(z);
  float theta = atan(z.y, z.x);
  return pow(r, n) * vec2(cos(n * theta), sin(n * theta));
}

void main() {
  vec2 uv = (gl_FragCoord.xy - 0.5 * uResolution.xy) / min(uResolution.x, uResolution.y);
  
  // Apply transformations for "perception shift" effect
  // Device tilt adds to rotation
  float rotAngle = uTime * 0.1 + uBass * 0.5 + uDeviceTilt.x * 2.0;
  uv = rotate2D(rotAngle) * uv;
  uv += uDeviceTilt * 0.5; // Subtle position shift
  
  // Warping based on radial distance
  float r = length(uv);
  float warp = 1.0 + 0.3 * sin(r * 5.0 - uTime * 0.5);
  uv *= warp;
  
  // Scale
  uv = uv * 2.0 / uZoom;
  
  vec2 z = uv;
  vec2 c = vec2(
    0.355 + sin(uTime * 0.1) * 0.1,
    0.355 + cos(uTime * 0.13) * 0.1
  );
  
  float iterations = 0.0;
  float trap = 1e10;
  
  for (int i = 0; i < MAX_ITERATIONS; i++) {
    if (dot(z, z) > 100.0) break;
    
    // Morphing between different fractal types based on time
    float morphPhase = sin(uTime * 0.15) * 0.5 + 0.5;
    
    // Type 1: Modified Julia
    vec2 z1 = cmul(z, z) + c;
    
    // Type 2: Burning Ship variant
    vec2 z2 = vec2(abs(z.x), abs(z.y));
    z2 = cmul(z2, z2) + c;
    
    // Blend between types
    z = mix(z1, z2, morphPhase * uMid);
    
    // Add subtle rotation per iteration
    z = rotate2D(0.01 + uHigh * 0.02) * z;
    
    // Orbit trap for additional visual interest
    trap = min(trap, length(z - vec2(0.0, 1.0)));
    
    iterations += 1.0;
  }
  
  float t = iterations / float(MAX_ITERATIONS);
  float trapNorm = 1.0 - exp(-trap * 2.0);
  
  vec3 col;
  
  if (t >= 1.0) {
    // Inside - use trap distance for color
    col = mix(uColorPrimary, uColorSecondary, trapNorm);
    col *= 0.5 + uBass * 0.3;
  } else {
    // Outside
    float phase = t * 4.0 + trapNorm * 2.0 + uTime * 0.05;
    
    col = uColorPrimary * (0.3 + 0.3 * sin(phase));
    col += uColorSecondary * (0.3 + 0.3 * sin(phase + 2.094));
    col += uColorAccent * (0.3 + 0.3 * sin(phase + 4.188));
    
    // Add trapped orbit visualization
    col += uColorAccent * (1.0 - trapNorm) * 0.5;
    
    // Audio-reactive pulse
    col *= 1.0 + uAudioAverage * 0.3;
  }
  
  // Radial fade for perspective shift feeling
  float radialFade = 1.0 - smoothstep(0.5, 1.5, length(vUv * 2.0 - 1.0));
  col *= 0.7 + radialFade * 0.3;
  
  // Subtle scanlines for digital perception feel
  float scanline = 0.95 + 0.05 * sin(gl_FragCoord.y * 2.0 + uTime * 2.0);
  col *= scanline;
  
  gl_FragColor = vec4(col, 1.0);
}

